import { ObservableLevelEnumValue, ObservableLevelsEnum } from '../data/enums';
import { ObservationModel } from '../data/types';

class ObservationHelpers {
  /**
   * A part reading taken before its bucket was re-homed keeps the superseded part id, while the
   * device only holds the replacement part under the same external id (#11006). The external id
   * is the part's identity on the device and on the wire (sanitizeObservationsForWire keys on it),
   * so at the equipment-part level it decides the match on its own. Every other level still needs
   * both ids.
   */
  static isObservationForEntity({
    observation,
    dataLevel,
    entityId,
    entityExternalId,
  }: {
    observation: ObservationModel;
    dataLevel: ObservableLevelEnumValue;
    entityId: number;
    entityExternalId: string;
  }): boolean {
    if (
      observation.dataLevelId !== dataLevel ||
      observation.entityExternalId !== entityExternalId
    ) {
      return false;
    }

    return (
      observation.entityId === entityId ||
      (dataLevel === ObservableLevelsEnum.equipmentPart &&
        entityExternalId !== '')
    );
  }

  /**
   * Keeps only the first row of each non-collection field - the row every read and draft
   * flush targets, and so the one the push ships (sanitizeObservationsForWire). A later twin,
   * such as a value re-entered before #11006 against a re-homed part, is dropped. Collections
   * keep every row.
   */
  static keepFirstRowPerField(
    observations: ObservationModel[],
  ): ObservationModel[] {
    const seenFields = new Set<string>();
    return observations.filter((observation) => {
      if (observation.observableItem?.isCollection) {
        return true;
      }
      const field = [
        observation.observableItemId,
        observation.entityExternalId,
        observation.dataLevelId,
        observation.unitId,
      ].join('|');
      if (seenFields.has(field)) {
        return false;
      }
      seenFields.add(field);
      return true;
    });
  }

  static isObservationEqual({
    observation,
    dataLevel,
    entityId,
    entityExternalId,
    paths,
  }: {
    observation: ObservationModel;
    dataLevel: ObservableLevelsEnum;
    entityId: number;
    entityExternalId: string;
    paths: string[];
  }): boolean {
    return (
      ObservationHelpers.isObservationForEntity({
        observation,
        dataLevel,
        entityId,
        entityExternalId,
      }) && paths.includes(observation.observableItem.identifier)
    );
  }
}

export default ObservationHelpers;
